import React, { useState, useEffect, useRef } from 'react';
import './AutocompleteInput.css';

const AutocompleteInput = ({ value, onChange, options, placeholder, className }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(0);
  const ref = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filtered = query
    ? options.filter(o => o.toLowerCase().startsWith(query.toLowerCase()))
    : options;
  const visible = filtered.slice(0, 20);

  // Filtre değişince ya da liste açılınca highlight'ı resetle
  useEffect(() => {
    if (visible.length === 0) { setHighlightIdx(0); return; }
    // Mevcut value listede varsa onu vurgula
    const idx = visible.findIndex(o => o === value);
    setHighlightIdx(idx >= 0 ? idx : 0);
  }, [query, open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Vurgulanan option'ı görünür alana scroll et
  useEffect(() => {
    if (!open || !dropdownRef.current) return;
    const el = dropdownRef.current.querySelector(`[data-ac-idx="${highlightIdx}"]`);
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightIdx, open]);

  const handleFocus = () => {
    setOpen(true);
    setQuery(value || '');
  };

  const handleChange = (q) => {
    setQuery(q);
    onChange(q);
    if (!open) setOpen(true);
  };

  const handleSelect = (opt) => {
    onChange(opt);
    setQuery(opt);
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      if (visible.length === 0) return;
      setHighlightIdx(i => Math.min(visible.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) return;
      if (visible.length === 0) return;
      setHighlightIdx(i => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      if (open && visible.length > 0) {
        e.preventDefault();
        handleSelect(visible[highlightIdx] || visible[0]);
      }
    } else if (e.key === 'Escape') {
      if (open) { e.preventDefault(); setOpen(false); }
    } else if (e.key === 'Tab') {
      // Tab: dropdown'u kapat, varsayılan focus akışına bırak
      if (open) setOpen(false);
    }
  };

  return (
    <div className="ac-wrap" ref={ref}>
      <input
        className={className || 'ac-input'}
        type="text"
        value={open ? query : (value || '')}
        onFocus={handleFocus}
        onChange={e => handleChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
      />
      {open && visible.length > 0 && (
        <div className="ac-dropdown" ref={dropdownRef}>
          {visible.map((opt, i) => (
            <div
              key={opt}
              data-ac-idx={i}
              className={`ac-opt${opt === value ? ' selected' : ''}${i === highlightIdx ? ' highlight' : ''}`}
              onMouseEnter={() => setHighlightIdx(i)}
              onMouseDown={(e) => { e.preventDefault(); handleSelect(opt); }}
            >
              {opt}
            </div>
          ))}
          {filtered.length > 20 && (
            <div className="ac-more">+{filtered.length - 20} sonuc daha...</div>
          )}
        </div>
      )}
    </div>
  );
};

export default AutocompleteInput;
